import { PageOrderEnum } from "@/lib/enum/pageOrder.enum";
import projectService from "@/services/mrfiktiv/services/projectService";
import {
  MrfiktivCreateProjectDtoGen,
  MrfiktivCustomFieldListElementDtoGen,
  MrfiktivProjectConfigurationViewModelGen,
  MrfiktivProjectControllerGetParamsGen,
  MrfiktivProjectCustomViewDtoGen,
  MrfiktivProjectCustomViewViewmodelGen,
  MrfiktivProjectViewModelGen,
  MrfiktivShortUserViewModelGen,
  MrfiktivTimestampGen,
  MrfiktivUpdateProjectDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import store from "@/store/VuexPlugin";
import { Action, Module, Mutation, getModule } from "vuex-module-decorators";
import { BasePagination, FilterTypes, IPaginationFilterListElement } from "./base-pagination.store";
import { PartnerUserModule } from "./partner-user.store";

export class Project implements MrfiktivProjectViewModelGen {
  /** identifier of project */
  id?: string;

  /** identifier of partner that manages project */
  partnerId = "";

  /** identifier of user that created project */
  userId = "";

  /** timestamp of the project */
  timestamp: MrfiktivTimestampGen = { created: "", lastModified: "" };

  /** the name of the project */
  title = "";

  /** the description of the project */
  description?: string;

  /** the configuration of the project */
  configuration: MrfiktivProjectConfigurationViewModelGen = {
    customFieldConfig: [],
    isTemplate: false,
    views: []
  };

  /**
   * when the ticket was closed
   * @format date-time
   */
  closedAt?: string;

  /** user id that closed the ticket */
  closedBy?: string;

  /** user details that closed the ticket */
  closedByDetails?: MrfiktivShortUserViewModelGen;

  constructor(project?: MrfiktivProjectViewModelGen | MrfiktivCreateProjectDtoGen | MrfiktivUpdateProjectDtoGen) {
    Object.assign(this, project);
  }

  private get customFieldConfigDto(): MrfiktivCustomFieldListElementDtoGen[] {
    return this.configuration.customFieldConfig.map(c => {
      return {
        configuration: c.configuration,
        customField: c.customField.id
      };
    });
  }

  get createDto(): MrfiktivCreateProjectDtoGen {
    const dto: MrfiktivCreateProjectDtoGen = {
      title: this.title,
      description: this.description,
      configuration: {
        isTemplate: this.configuration.isTemplate,
        customFieldConfig: this.customFieldConfigDto,
        views: []
      }
    };

    return dto;
  }

  get updateDto(): MrfiktivUpdateProjectDtoGen {
    const views = [];
    for (const view of this.configuration.views) {
      views.push(this.createCustomViewsDto(view));
    }

    const dto: MrfiktivUpdateProjectDtoGen = {
      title: this.title,
      description: this.description,
      configuration: {
        isTemplate: this.configuration.isTemplate,
        customFieldConfig: this.customFieldConfigDto,
        views: views
      }
    };

    return dto;
  }

  /**
   * Ads a new view to the config
   * @param customView
   */
  addView(customView: MrfiktivProjectCustomViewViewmodelGen) {
    this.configuration.views.push(customView);
  }

  /**
   * Updates a new view in the views array using viewId and updatedView
   * @param updatedView
   * @param viewId
   */
  updateView(updatedView: MrfiktivProjectCustomViewViewmodelGen, viewId: string) {
    const index = this.configuration.views.findIndex(view => view.id === viewId);

    if (index !== -1) {
      // Replace the old view with the updated one
      this.configuration.views[index] = updatedView;
    } else {
      // Handle the case where the view to update is not found
      console.error("View not found for update:", updatedView);
    }
  }

  createCustomViewsDto(customView: MrfiktivProjectCustomViewViewmodelGen): MrfiktivProjectCustomViewDtoGen {
    return {
      type: customView.type,
      title: customView.title,
      boardColumn: customView?.boardColumn,
      groupBy: customView?.groupBy,
      calendarStart: customView?.calendarStart,
      calendarEnd: customView?.calendarEnd,
      values: customView.values,
      filters: customView.filters
    };
  }
}

@Module({
  dynamic: true,
  namespaced: true,
  name: "project",
  store
})
export class ProjectStore extends BasePagination<MrfiktivProjectViewModelGen, MrfiktivProjectControllerGetParamsGen> {
  protected _pageOrder: PageOrderEnum = PageOrderEnum.DESCENDING;
  protected _itemsPerPage = 100;
  protected _totalPages = 0;
  protected _paginationList: MrfiktivProjectViewModelGen[] = [];
  protected _currentPage = 1;
  protected _totalItems = 0;
  protected _isLoadAll = true;
  protected _filterList: IPaginationFilterListElement[] = [
    { key: "_id", type: FilterTypes.OBJECT_ID, displayName: "objects.project.id" },
    {
      key: "userId",
      type: FilterTypes.OBJECT_ID,
      displayName: "objects.project.userId",
      config: {
        items: PartnerUserModule.paginationList,
        mapItemToComponent: (item: any) => {
          return { item };
        },
        itemValue: "id",
        component: "refs-user"
      }
    },
    { key: "title", type: FilterTypes.STRING, displayName: "objects.project.title" },
    { key: "description", type: FilterTypes.STRING, displayName: "objects.project.description" },
    {
      key: "configuration.views.title",
      type: FilterTypes.STRING,
      displayName: "objects.project.configuration.views.title"
    }
  ];

  private _project: MrfiktivProjectViewModelGen | undefined = undefined;
  private _projectIdMap = new Map<string, MrfiktivProjectViewModelGen>();
  protected _documentMapConfig = { id: this._projectIdMap };

  get projects() {
    return this._paginationList;
  }

  @Mutation
  private _mutateProject(request: MrfiktivProjectViewModelGen) {
    this._project = request;
  }

  @Action
  protected async loadDocuments(query: MrfiktivProjectControllerGetParamsGen) {
    return projectService.getAll({ ...query });
  }

  @Action
  async create(data: { partnerId: string; data: MrfiktivCreateProjectDtoGen }) {
    const res = await projectService.create(data.partnerId, data.data);

    this.context.commit("_mutateProject", res);
    this.addToList(res);

    return res;
  }

  @Action
  async delete(data: { partnerId: string; projectId: string }) {
    const res = await projectService.delete(data.partnerId, data.projectId);
    this.context.commit("_mutateProject", res);

    this.removeInList(res);

    return res;
  }

  @Action
  async getOne(data: { partnerId: string; projectId: string }) {
    const res = await projectService.getOne(data.partnerId, data.projectId);

    this.context.commit("_mutateProject", res);

    return res;
  }

  @Action
  async update(data: { partnerId: string; projectId: string; data: MrfiktivUpdateProjectDtoGen }) {
    const res = await projectService.update(data.partnerId, data.projectId, data.data);

    this.context.commit("_mutateProject", res);
    this.replaceInList(res);

    return res;
  }

  @Action
  async getAll(data: { partnerId: string }) {
    await this.fetchAllFromBeginning({
      partnerId: data.partnerId
    });
  }
}

export const ProjectModule = getModule(ProjectStore);
